import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSocket, useSources } from 'hooks';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';

import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Card from '@material-ui/core/Card';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Fab from '@material-ui/core/Fab';

import { Edit, Delete, ArrowUpward, ArrowDownward } from '@material-ui/icons';
import { MdRemoveRedEye, MdAdd, MdInfo, MdCloudDownload } from 'react-icons/md';
import { ImNewspaper, ImCalendar, ImUser, ImTwitter } from 'react-icons/im';
import { RiTeamFill } from 'react-icons/ri';
import { GiBasketballBall } from 'react-icons/gi';
import { FaSync } from 'react-icons/fa';

import PushApiLog from '../containers/PushApiLog';
import PushApiInfo from '../containers/PushApiInfo';
import Media from '../../Template/components/Media';
import withSpinner from '../../components/withSpinner';
import { RenderIconButton } from '../../Channel/shared/components/GenericComponents';
import { ConfirmDialog, ErrorDialog, Table } from 'components';
import TwitterDialog from './TwitterDialog';

import API from 'utils/api';
import { classes } from 'components/utils';

import styles from './Leagues.module.scss';

const servicesConfig = {
  TEAM_PROFILES: { icon: RiTeamFill, title: 'team' },
  SEASON: { icon: ImCalendar },
  SCHEDULE: { icon: GiBasketballBall },
  PUSH_EVENTS: { icon: MdCloudDownload, title: 'push', push: true },
  PLAYER_PROFILES: { icon: ImUser, title: 'player' },
  NEWS: { icon: ImNewspaper },
  TWEETS: { icon: ImTwitter, title: 'tweets' },
};

const SportsApiPush = () => {
  const [logApi, setLogApi] = useState(null);
  const [dialog, setDialog] = useState({});
  const [dialogCounter, setDialogCounter] = useState({});

  const [errorMessage, setErrorMessage] = useState('');
  const [sports, setSports] = useState([]);

  const [league, setLeague] = useState({
    leagueName: '',
    leagueFullName: '',
    homePosition: 'left',
    image: '',
    imageUrl: '',
    selectedLeague: '',
    sport: '',
    periodName: '',
    sportError: false,
  });

  const twitter = useRef({
    league: null,
    serviceId: null,
    disabled: false,
  });

  const sources = useSources();

  useSocket('sportsapi', null, { refreshLeagues: () => updateTable() });

  useEffect(() => {
    refreshSportsList();
  }, [refreshSportsList]);

  const tableRef = useRef();

  const changeDialogState = state => {
    setDialog({ league: state });
    setErrorMessage('');
  };

  const refreshSportsList = useCallback(() => {
    API.getSports(
      {
        select: 'name active logo',
        populate: ['logo'],
        ignoreDependencies: true,
      },
      sources
    ).then(({ data }) => {
      if (data && data.docs) {
        setSports(data.docs);
      }
    });
  }, [sources]);

  const editLeague = (leagueId, data) => API.editLeague(leagueId, data, sources);
  

  const editLeaguePosition = (leagueId, data) => API.editLeaguePosition(leagueId, data, sources);

  const setServiceActive = (leagueId, serviceIdx, active) =>
    API.editLeague(
      leagueId,
      {
        serviceUpdate: { serviceIdx, disabled: !active },
      },
      sources
    );

  const createLeague = data => API.createLeague(data, sources);

  const refreshLeagues = useCallback(
    (name, pageSize, page) =>
      API.getLeagues(
        {
          name,
          select:
            'name fullName homePosition logo active services order periodName sport',
          populate: ['logo', 'sport'],
          sort:'order',
          pageSize,
          page,
        },
        sources
      ),
    [sources]
  );

  const startPush = leagueId => API.startPush({ id: leagueId }, sources);
  const stopPush = leagueId => API.stopPush({ id: leagueId }, sources);

  const updateImageToSend = (image, imageUrl) => {
    setLeague({ ...league, image, imageUrl });
  };

  const fetchData = useCallback(
    async query => {
      const { page, pageSize, search } = query;

      const name = search && search.length ? search : {};
      const response = await refreshLeagues(name, pageSize, page);

      return response;
    },
    [refreshLeagues]
  );

  const deleteLeague = league => API.deleteLeague(league, sources);

  const mediaContainer = {
    backgroundImage: `url("./images/${league.imageUrl}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  const updateTable = () => {
    if (tableRef && tableRef.current) {
      tableRef.current.reload(); 
    }
  };

  const fillDialogForm = row => {
    setLeague({
      leagueName: row.name || '',
      leagueFullName: row.fullName || '',
      homePosition: row.homePosition || '',
      imageUrl: row.logo
        ? `https://${row.logo.container}.s3.amazonaws.com/${row.logo.blob}`
        : '',
      selectedLeague: row._id,
      sport: row.sport && row.sport._id ? row.sport._id : '',
      periodName: row.periodName || '',
    });
  };

  const cleanForm = () => {
    setLeague({
      leagueName: '',
      leagueFullName: '',
      homePosition: 'left',
      image: '',
      imageUrl: '',
      selectedLeague: '',
      sport: '',
      periodName: '',
      sportError: false,
    });
  };

  const renderServices = (leagueId, services) => {
    if (!services || !services.length) {
      return null;
    }

    return (
      <div className={styles.renderServices}>
        {services.map((service, index) => {
          const { type, status, provider } = service;
          const config = servicesConfig[type.toUpperCase()];

          if (config) {
            const title = config.title || type;

            let Icon = config.icon;
            let disabled = service.disabled;

            if (config.push) {
              if (status === 'initializing' || status === 'shuttingDown') {
                Icon = withSpinner(FaSync);
              }

              disabled = !status || status === 'idle';
            }

            return (
              <RenderIconButton
                key={type}
                title={`${title.charAt(0).toUpperCase()}${title
                  .slice(1)
                  .toLowerCase()} service - ${provider}`}
                icon={<Icon />}
                placement="top"
                color={disabled ? 'primary' : 'secondary'}
                clickEvent={() => {
                  if (config.title !== 'tweets') {
                    setServiceActive(leagueId, index, disabled);

                    if (config.push) {
                      disabled ? startPush(leagueId) : stopPush(leagueId);
                    }
                  } else {
                    setDialog({ twitter: { leagueId } });
                    twitter.current.league = leagueId;
                    twitter.current.serviceId = index;
                    twitter.current.disabled = disabled;
                  }
                }}
              />
            );
          }

          return null;
        })}
      </div>
    );
  };

  const renderReposition = (state) => {
    setDialogCounter(state);
  };

  const renderOptions = league => {
    const { hasDependencies, pushStatus = 'idle', services = [] } = league;

    const pushService = services.find(({ type }) => type === 'PUSH_EVENTS');
    
    const options = [
      <RenderIconButton
        key="edit"
        clickEvent={() => {
          fillDialogForm(league);
          changeDialogState('edit');
        }}
        title="Edit League"
        placement="top"
        icon={<Edit />}
        color="primary"
      />,
    ];

    if (!hasDependencies && !services.length) {
      options.push(
        <RenderIconButton
          key="delete"
          clickEvent={() => setDialog({ deleteLeague: league })}
          title="Delete League"
          placement="top"
          icon={<Delete />}
          color="primary"
        />
      );
    }

    if (pushService) {
      options.push(
        <RenderIconButton
          key="pushInfo"
          clickEvent={() => setLogApi({ ...league, showInfo: true })}
          title="Show push events config"
          placement="top"
          icon={<MdInfo />}
          color="primary"
        />
      );

      if (pushStatus === 'running') {
        options.push(
          <RenderIconButton
            key="pushEvents"
            clickEvent={() => setLogApi(league)}
            title="View real-time push events"
            placement="top"
            icon={<MdRemoveRedEye />}
            color="primary"
          />
        );
      }
    }

    return options;
  };

  return (
    <div>
      <div className={styles.containerFloatButton}>
        <Fab
          variant="extended"
          size="small"
          color="primary"
          aria-label="add"
          onClick={() => {
            changeDialogState('create');
          }}
          className={styles.floatButton}
        >
          <MdAdd className={styles.addIcon} />
          Add new League
        </Fab>
      </div>

      <Table
        tableRef={tableRef}
        data={fetchData}
        title="Leagues"
        filtering={false}
        sorting={false}
        pageSizeOptions={[5, 10, 20, 40, 100]}
        pageSize={20}
        search={true}
        columns={[
          {
            title: 'Order',
            field: 'order',
            defaultSort: 'desc',
            filtering: false,
            render: ({ _id, order }) => (
              <div className={styles.orderField}>
                <div className={styles.orderCounter} onClick={()=>{renderReposition({_id, counter:order, status:true})}}>
                  {order}
                </div>
              <div className={styles.orderIcons}>
                <RenderIconButton
                  clickEvent={() => editLeague(_id, { order: order - 1 })}
                  title={'Move up'}
                  placement={'top'}
                  icon={<ArrowUpward fontSize={'small'} />}
                  color={'primary'}
                />
                <RenderIconButton
                  clickEvent={() => editLeague(_id, { order: order + 1 })}
                  title={'Move down'}
                  placement={'top'}
                  icon={<ArrowDownward fontSize={'small'} />}
                  color={'primary'}
                />
              </div>
              </div>
            ),
          },
          { title: 'Name', field: 'name', weight: 2 },
          {
            title: 'Full Name',
            field: 'fullName',
            weight: 2,
          },
          {
            title: 'Logo',
            field: 'logo',
            sorting: false,
            filtering: false,
            render: ({ logo }) => (
              <Card
                className={styles.card}
                style={
                  logo
                    ? {
                        backgroundImage: `url(https://${logo.container}.s3.amazonaws.com/${logo.blob})`,
                      }
                    : undefined
                }
              >
                {logo ? null : 'None Selected'}
              </Card>
            ),
          },
          {
            title: 'Home Position',
            field: 'homePosition',
          },
          {
            title: 'Period Name',
            field: 'periodName',
          },
          {
            title: 'Sport',
            field: 'sport.name',
          },
          {
            title: 'Services',
            field: 'services',
            weight: 1.5,
            sorting: false,
            render: ({ _id, services }) => renderServices(_id, services),
          },
          {
            title: 'Active',
            field: 'active',
            sorting: false,
            render: ({ _id, active, hasEvent }) => (
              <div>
                <Switch
                  color="primary"
                  checked={!!active}
                  onChange={() =>
                    hasEvent
                      ? setDialog({ alertOpen: true })
                      : editLeague(_id, { active: !active })
                  }
                />
              </div>
            ),
          },
          {
            title: 'Options',
            sorting: false,
            render: rowData => <div>{renderOptions(rowData)}</div>,
          },
        ]}
      />

      {logApi ? (
        logApi.showInfo ? (
          <PushApiInfo
            key={logApi._id}
            api={logApi}
            collapse={false}
            onClose={() => setLogApi(null)}
          />
        ) : (
          <PushApiLog key={logApi._id} api={logApi} onClose={() => setLogApi(null)} />
        )
      ) : null}

      <ErrorDialog
        open={!!dialog.alertOpen}
        onClose={() => {
          setDialog({});
        }}
        title="You cannot disable this league."
        message={'There is one or more games associated with this league.'}
      />

      <Dialog
        open={!!dialog.league}
        onClose={() => {
          changeDialogState();
          cleanForm();
        }}
        fullWidth={true}
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="league-dialog"
        className={styles.leagueDialog}
      >
        <DialogTitle id="league-dialog">
          {dialog.league === 'edit' ? 'Edit League' : 'Add League'}
        </DialogTitle>
        <DialogContent dividers>
          <div className={styles.dialogContentLeft}>
            <TextField
              className={styles.textField}
              {...(errorMessage !== ''
                ? { error: true, helperText: `${errorMessage}` }
                : {})}
              id="standard-full-width"
              label="League Name"
              name="name"
              placeholder="Enter League name"
              inputProps={{ maxLength: 100 }}
              value={league.leagueName}
              onChange={event => {
                setLeague({ ...league, leagueName: event.target.value });
              }}
              required
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              className={styles.textField}
              {...(errorMessage !== ''
                ? { error: true, helperText: `${errorMessage}` }
                : {})}
              id="standard-full-width"
              label="League Full Name"
              name="fullname"
              placeholder="Enter League full name"
              inputProps={{ maxLength: 100 }}
              value={league.leagueFullName}
              onChange={event => {
                setLeague({ ...league, leagueFullName: event.target.value });
              }}
              required
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
            <FormLabel component="legend">Home Side Position</FormLabel>
            <RadioGroup
              aria-label="homePosition"
              name="homePosition"
              value={league.homePosition}
              onChange={event =>
                setLeague({ ...league, homePosition: event.target.value })
              }
            >
              <FormControlLabel value="left" control={<Radio />} label="Left Side" />
              <FormControlLabel value="right" control={<Radio />} label="Right Side" />
            </RadioGroup>

            <TextField
              className={styles.textFieldPeriod}
              id="standard-full-width"
              label="Period Name"
              name="periodName"
              placeholder="Enter Period name"
              inputProps={{ maxLength: 100 }}
              value={league.periodName}
              onChange={event => {
                setLeague({ ...league, periodName: event.target.value });
              }}
              required
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />

            <InputLabel id="demo-simple-select-label" className={styles.inputSport}>
              Select Sport*
              {league.sportError ? (
                <span className={styles.errorSportMissing}>Select a Sport</span>
              ) : (
                ''
              )}
            </InputLabel>
            <Select
              id="demo-simple-select"
              value={league.sport}
              onChange={event => {
                setLeague({ ...league, sport: event.target.value });
              }}
            >
              <MenuItem value={''} className="select">
                <div className="select">
                  <em>Doesn't Have a Sport</em>
                </div>
              </MenuItem>
              {sports.map(row => (
                <MenuItem key={row._id} value={row._id} name={row.name}>
                  <div className="select">{row.name}</div>
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={styles.dialogContentRight}>
            <Typography variant="subtitle2" gutterBottom align="center">
              League Logo
            </Typography>
            <div
              className={
                league.imageUrl !== ''
                  ? classes(
                      mediaContainer,
                      ' icon-container media-container',
                      styles.image
                    )
                  : classes('icon-container media-container', styles.image)
              }
            >
              <Media
                size={200}
                mediaType="image"
                mediaFile={league.image}
                mediaUrl={league.imageUrl}
                onChange={updateImageToSend}
                onFetch={updateImageToSend}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              changeDialogState();
              cleanForm();
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              if (league.leagueName && league.leagueName !== '') {
                if (league.sport) {
                  const newLeague = {
                    name: league.leagueName,
                    fullname: league.leagueFullName,
                    image: league.image,
                    homePosition: league.homePosition,
                    periodName: league.periodName || '',
                    sport: league.sport,
                  };

                  if (dialog.league === 'edit') {
                    editLeague(league.selectedLeague, newLeague);
                  } else {
                    createLeague(newLeague);
                  }

                  cleanForm();
                  changeDialogState();
                } else {
                  setLeague({ ...league, sportError: true });
                }
              } else {
                setErrorMessage('League name has to be filled');
              }
            }}
            disabled={'selectedElement' === 'undefined'}
          >
            Save League
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={dialogCounter.status===true}
        onClose={() => {
          setDialogCounter({status:false});
        }}
        fullWidth={true}
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="league-dialog-relocate"
        className={styles.leagueDialog}
      >
        <DialogTitle id="league-dialog">
          {'Change League position'}
        </DialogTitle>
        <DialogContent dividers>
          <div className={styles.dialogContentLeft}>
            <TextField
              className={styles.textFieldPeriod}
              type="number"
              id="standard-full-width"
              label="New league position"
              name="position"
              placeholder="Enter the new position"
              inputProps={{ maxLength: 4 }}
              value={dialogCounter.counter}
              onChange={event => {
                setDialogCounter({ ...dialogCounter, counter: event.target.value });
              }}
              required
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
       
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogCounter({status:false});
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              editLeaguePosition(dialogCounter._id, {newPosition: dialogCounter.counter})
              setDialogCounter({status:false});
            }}
            disabled={'selectedElement' === 'undefined'}
          >
            Save League
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!dialog.deleteLeague}
        onClose={yes => {
          setDialog({});

          if (yes && dialog.deleteLeague._id) {
            deleteLeague(dialog.deleteLeague._id);
          }
        }}
        title=""
        message={[
          `Delete League ${dialog.deleteLeague ? dialog.deleteLeague.name : ''}?`,
        ]}
      />

      <TwitterDialog
        open={!!dialog.twitter}
        leagueId={dialog.twitter ? dialog.twitter.leagueId : null}
        serviceDisabled={!twitter.current.disabled}
        onChangeActiveService={async () => {
          const { league, serviceId, disabled } = twitter.current;
          await setServiceActive(league, serviceId, disabled);

          twitter.current.disabled = !disabled;
          setDialog({ twitter: { league } });
        }}
        onClose={() => {
          setDialog({});
        }}
      />
    </div>
  );
};

export default SportsApiPush;
